import TertiaryButton from "../buttons/TertiaryButton";
import InputLabel from "./InputLabel";
import { getMaxLength } from "../commonfunctions/getMaxLength";
import ErrorMessage from "../ErrorMessages/ErrorMessage";
import { validateInput } from "../commonfunctions/validateInput";
import { useEffect, useState } from "react";
import OTPVerification from "../Verifications/OTPVerification";
import Image from "../images/Image";
import InputFramer from "../framerMotion/InputFramer";
import InputShimmer from "./InputShimmer";
import useTranslation from "../CustomHooks/useTranslation";
import { inputFieldContent } from "./InputFieldContent";
interface InputItem {
  labelName: string;
  id: string;
}

interface InputFieldProps {
  inputItem: InputItem;
  errorMessage?: string;
  required?: boolean;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  fieldError?: string;
  tabIndex: number; // Total number of input fields to determine navigation
  maxLength?: number;
  testId?: string;
  disabled?: boolean;
  value?: string;
  handleVerify: (id: string, resend: boolean) => void;
  handleSubmitOTP: () => void;
  isVerified: string;
  ButtonLoading?: boolean;
  editLoading?: boolean;
  isOpenOtpModel?: boolean;
  setIsOpenOtpModel?: React.Dispatch<React.SetStateAction<boolean>>;
  validation?: boolean;
  mapping?: boolean;
}

export default function InputfieldWithVerification({
  inputItem,
  errorMessage,
  onChange,
  required,
  tabIndex,
  fieldError,
  maxLength,
  testId,
  disabled,
  handleVerify,
  handleSubmitOTP,
  isOpenOtpModel,
  setIsOpenOtpModel,
  isVerified,
  value,
  ButtonLoading,
  editLoading,
  validation = true,
  mapping
}: InputFieldProps) {
  const { labelName, id } = inputItem;
  const [isError, setIsError] = useState("");
  const [inputValue, setInputValue] = useState<string>("");
  const [isModelOpen, setIsModelOpen] = useState(false);
  const { translate, replace, notEnglish } = useTranslation(inputFieldContent);
  const handleClickVerify = () => {
    setIsModelOpen(true);
    if (id === "email_address" || id === "official_email_address" || id === 'contact_email' || id === 'recovery_email_address') {
      let inputValidationErrors: Record<string, string> = {};
      if (validation) {
        inputValidationErrors = validateInput({
          [id]: inputValue ?? "",
        });

        if (Object.keys(inputValidationErrors).length > 0) {
          setIsError(inputValidationErrors[id] || "");
          return;
        }
      } else {
        const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (inputValue !== '' && !regex.test(inputValue)) {
          if(id === 'contact_email'){
            setIsError("Invalid contact email address");
          return;
          }
          setIsError("Please enter a valid email address");
          return;
        }
      }
      handleVerify(id, false);
    }
  };

  // Email validation regex - only allow valid email characters
  const validEmailRegex = /^[a-zA-Z0-9._@\-+]*$/;

  // Handle change event with max length restriction and character validation for emails
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const maxInputLength = getMaxLength(id);
    const { value: newValue } = event.target;
    setIsError("");

    let processedValue = newValue;

    if (id === "email_address" || id === "official_email_address" || id === 'recovery_email_address' || id==="contact_email") {
      // Remove any spaces from the email address
      processedValue = processedValue.replace(/\s/g, "");

      // Convert to lowercase
      processedValue = processedValue.toLowerCase();

      // Validate email characters - only proceed with valid characters
      if (!validEmailRegex.test(processedValue)) {
        return;
      }

      // Check for consecutive special characters (not allowed in emails)
      const consecutiveSpecialsRegex = /[._@\-+]{2,}/;
      if (consecutiveSpecialsRegex.test(processedValue)) {
        return; // Reject input with consecutive special characters
      }

      // Check specifically for consecutive dots
      if (processedValue.includes("..")) {
        return; // Reject input with multiple dots
      }

      // Check if there are multiple @ symbols
      const atSymbolCount = (processedValue.match(/@/g) || []).length;
      if (atSymbolCount > 1) {
        return; // Reject input with multiple @ symbols
      }
    }

    // Only apply maxLength if the value exceeds it
    // Don't manually trim the value - let the maxLength attribute handle it
    setInputValue(processedValue);

    if (onChange) {
      // Create a new synthetic event with the processed value
      const syntheticEvent = {
        ...event,
        target: {
          ...event.target,
          value: processedValue
        }
      } as React.ChangeEvent<HTMLInputElement>;

      onChange(syntheticEvent); // Trigger the parent onChange handler
    }
  };

  useEffect(() => {
    if (!isOpenOtpModel) {
      setIsModelOpen(false);
    }
  }, [isOpenOtpModel]);

  // Update local inputValue when value prop changes
  useEffect(() => {
    if (value !== undefined) {
      setInputValue(value);
    }
  }, [value]);

  return (
    <InputFramer>
      <InputLabel labelName={labelName} id={id} required={required} />
      {/* if error occers change it to border color border-negative */}

      {editLoading ? (
        <InputShimmer className={mapping ? 'w-full' : ''} />
      ) : (
        <div className={`${mapping ? 'w-full' : 'w-80'}  relative `}>
          <input
            id={id}
            data-testid={testId || id}
            className={`w-full h-14 py-5 ${isVerified
              ? "pr-[45px]"
              : ButtonLoading
                ? "pr-[53px]"
                : !notEnglish ? 'pr-[68px]' : (notEnglish && value !== '') ? "pr-[120px]" : 'pr-[20px]'
              }  disabled:cursor-not-allowed pl-2.5 focus:pl-[9px] disabled:bg-disable_fill disabled:border-disable_stroke border rounded-lg text-subTitle outline-none focus:border-2 ${errorMessage || fieldError || isError
                ? "border-negative"
                : "border-P_light_active"
              }`}
            placeholder={translate(`Enter ${labelName.toLocaleLowerCase()}`)}
            onChange={handleChange} // Use the updated onChange handler
            tabIndex={tabIndex + 1} // Ensure correct tab order for navigation
            autoComplete="off"
            maxLength={maxLength} // Let the native maxLength handle the restriction
            disabled={disabled}
            value={value || inputValue} // Use controlled component approach
          />
          <span
            className={`absolute ${isVerified
              ? " top-[19px] right-[20px]"
              : ButtonLoading
                ? "top-[15px] right-[20px]"
                : !notEnglish ? "top-2 right-[10px]" : "w-[7rem] top-2 right-[20px]"
              }`}
          >
            {isVerified === 'true' ? (
              <Image src="success-tick" className="h-5 w-5" />
            ) : disabled && !ButtonLoading ? (
              ""
            ) : (
              (value || inputValue)?.length !== 0 && (
                <TertiaryButton
                  testId={`${id}_verify`}
                  buttonName={translate("Verify")}
                  id="Verify"
                  handleClick={handleClickVerify}
                  loading={ButtonLoading}
                  className={'w-full '}
                />
              )
            )}
          </span>
        </div>
      )}
      {/* if error comes please change show the error message  */}
      {(errorMessage || isError) && !disabled && (
        <ErrorMessage errorMessage={errorMessage || isError} />
      )}
      {isOpenOtpModel && isModelOpen && (
        <OTPVerification
          handleSubmitOTP={handleSubmitOTP}
          handleClickResendOTP={handleVerify}
          instructionText={replace(`We have sent a code to {{email_or_phone}}`, {
            email_or_phone: `${value || inputValue}`,
          })}
          otp_id={id}
          handleClose={() => {
            setIsOpenOtpModel && setIsOpenOtpModel(false);
            setIsModelOpen(false);
          }}
        />
      )}
    </InputFramer>
  );
}